import { useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { type RouteParams } from './RouteParams';
import { featureRouteMap } from './featureRouteMap';
import { serializeWebHistoryState } from './serializeWebHistoryState';

// Add two type definitions for navigating:
// (1) for known routes from RouteParams and
// (2) computed or dynamic URLs
type KnownRoute = keyof RouteParams;
const isKnownRoute = (route: string): route is KnownRoute => {
  return route in featureRouteMap;
};

// Create a map of pathname to route name for known routes
// i.e., Map { '/prescriptions': 'RoutePrescriptions' }
const pathnameToRouteName = new Map<string, KnownRoute>(
  (Object.keys(featureRouteMap) as KnownRoute[]).flatMap((routeName) => {
    const pathname = featureRouteMap[routeName]().web.pathname;
    return pathname ? [[pathname, routeName]] : [];
  }),
);

type NavigateFunction = {
  <NextRouteName extends KnownRoute>(route: NextRouteName, params?: RouteParams[NextRouteName]): void;
  (route: string, params?: any): void;
};

export const useNavigation = <RouteName extends keyof RouteParams = 'RouteAppTabNavigator'>() => {
  const navigateRRD = useNavigate();
  const location = useLocation();
  const routeParams = useParams();

  const navigate = useCallback<NavigateFunction>(
    (route: string, params?: any) => {
      let featureRoute;
      // Native uses a tab navigator where you navigate to RouteAppTabNavigator and pass { screen: 'RouteTabMyMeds' }.
      // Web doesn't have a tab navigator concept, so this unwraps it — it pulls out the actual tab route and navigates
      // there directly. RouteTabMyMeds gets special treatment because it's the only tab that takes params.
      if (route === 'RouteAppTabNavigator' && params && 'screen' in params) {
        const tabRouteName: KnownRoute = params.screen;
        if (!tabRouteName) return;
        // pass route params for tabs that have them
        if (tabRouteName === 'RouteTabMyMeds') {
          featureRoute = featureRouteMap[tabRouteName](params as RouteParams['RouteTabMyMeds']).web;
        } else {
          featureRoute = featureRouteMap[tabRouteName]().web;
        }
        // Looks up the pathname from featureRouteMap and navigates there.
      } else if (isKnownRoute(route)) {
        featureRoute = featureRouteMap[route](params).web;
        // Some places pass raw URLs like /some/path — just pass it straight through to React Router.
      } else {
        featureRoute = route;
      }
      // Support path-only navigation with optional replacement (e.g., AuthRequired: navigate(nextPath, { replace: true }))
      const replaceHistory = params && typeof params === 'object' && 'replace' in params && params.replace === true;
      const rawState =
        replaceHistory && params && typeof params === 'object'
          ? (() => {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars -- omit replacement from state
              const { replace, ...rest } = params;
              return Object.keys(rest).length ? rest : undefined;
            })()
          : params;
      const state = serializeWebHistoryState(rawState);
      navigateRRD(featureRoute, { replace: !!replaceHistory, state });
    },
    [navigateRRD],
  );

  const replace = useCallback(
    <T extends keyof RouteParams>(routeName: T, replaceParams?: RouteParams[T]) => {
      const featureRoute = featureRouteMap[routeName](replaceParams).web;
      navigateRRD(featureRoute, { replace: true });
    },
    [navigateRRD],
  );

  // Goes back one step in browser history, but if there's no history (e.g., user landed directly on a deep link),
  // sends them home instead of doing nothing.
  const goBack = useCallback(
    (_key?: string) => {
      void _key;
      if (window.history.length > 1) {
        navigateRRD(-1);
      } else {
        navigateRRD('/');
      }
    },
    [navigateRRD],
  );

  return {
    /**
     * @param route The native route name that you want to navigate to. Found in libs/shared/lib/nativeRouteNames.ts
     * @param params The params you want to pass. The relationship between the route name and passable params is found in libs/navigation/src/featureRouteMap.ts
     */
    navigate,

    /**
     * Get the name of the currently active route. Returns the canonical RouteName when the
     * current pathname has a known mapping, otherwise falls back to the raw pathname.
     */
    getCurrentRouteName: () => {
      return pathnameToRouteName.get(location?.pathname) ?? location?.pathname;
    },

    /**
     * Get the value of a URL parameter in a search string (i.e. '?next=/prescriptions') or
     * from a location state
     */
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
    getParam: <RouteKey extends keyof RouteParams[RouteName]>(paramName: RouteKey, fallbackValue?: any) => {
      const params = new URLSearchParams(location.search);
      const paramValue = location.state?.[paramName];
      // location.state is lost on a full page reload (e.g. a 401 hard-redirect + re-login), so fall
      // back to react-router path params, which live in the URL and survive reloads.
      const routeParamValue = routeParams[paramName as string];
      // ?? for the state/path/fallback chain so a valid falsy value (e.g. 0) isn't skipped; the
      // query-string lookup keeps || since URLSearchParams.get returns '' / null on a miss.
      return params.get(paramName as string) || (paramValue ?? routeParamValue ?? fallbackValue);
    },

    /**
     * No-op on web for now.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setParams: (_params?: Record<string, any>) => undefined,

    /**
     * No-op on web for now.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setOptions: (_options: any) => undefined,

    /**
     * Go to the previous page. NOTE this takes a key arg to maintain react-native compatibility, but this does nothing
     * on web, it will always instead take you to the previous page.
     */
    goBack,

    /**
     * No-op on web for now.
     */
    canGoBack: () => undefined,

    getRouteName: (routeName: RouteName) => routeName,

    /**
     * @param route The native route name that you want to navigate to. Found in libs/shared/lib/nativeRouteNames.ts
     * @param params The params you want to pass. The relationship between the route name and passable params is found in libs/navigation/src/featureRouteMap.ts
     */
    replace,
  };
};

export type Navigate = ReturnType<typeof useNavigation>['navigate'];
